import { ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import { useTranslation } from 'react-i18next';
import { ChatEnum, ChatState } from '../../../../Chat.enum.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import SchedulingRiskWarning from '../../../SchedulingRiskWarning/SchedulingRiskWarning.component.tsx';
import { isTemplateFreeAtSource } from 'src/pages/Wallet/hooks/useWalletSendGate';
import useWabTemplateScheduleAction from './WabTemplateScheduleAction.hook.ts';
import './WabTemplateScheduleAction.styles.scss';

export default function WabTemplateScheduleAction() {
  const { t } = useTranslation('chat/chat');
  const {
    setSelectedAction,
    scheduledWapiTemplate,
    setScheduledWapiTemplate,
    setIsSchedulePickerMode,
  } = useMessageBarContext();
  const { selectedChat, setTemplatePickerRequest } = useChatContext();
  const {
    loading,
    triggerDate,
    setTriggerDate,
    triggerTime,
    setTriggerTime,
    localDate,
    handleScheduleTemplate,
    paramValues,
    touchedParams,
    handleParamChange,
    handleParamBlur,
    parameters,
    previewBody,
    previewHeader,
    hasVariables,
    isScheduleDisabled,
    isDateInPast,
    headerMediaMissing,
  } = useWabTemplateScheduleAction();

  const isUtility = scheduledWapiTemplate?.template_type === 'UTILITY';

  /**
   * RET-2234 D-7: el badge Gratis/Con costo que la vuelta 2 retiró se
   * RESTITUYE, cableado al predicado COMPARTIDO — nada de la copia a mano
   * que traía este panel antes de este ticket
   * (`(selectedChat?.state === ACTIVE && isUtility) || hasFreeEntryPoint`,
   * ver git history) y que jamás habría sabido que la mitad UTILITY+ventana
   * caduca el 1-oct-2026.
   *
   * Este panel programa un envío a un `triggerDate`/`triggerTime` FUTURO, así
   * que se le pasa `at` = ese instante de disparo (hora local, el mismo que
   * arma `handleScheduleTemplate` para el payload) en vez de dejar que el
   * predicado use el reloj de "ahora" (D-5): una UTILITY agendada ANTES del
   * corte para disparar DESPUÉS tiene que decir "Con costo", porque así
   * llegará. Sin fecha elegida todavía, `at` es `undefined` y el predicado
   * cae a "ahora" (D-7) — el mismo veredicto que pintaba antes del retiro.
   */
  const triggerAt =
    triggerDate && triggerTime
      ? new Date(`${triggerDate}T${triggerTime}:00`)
      : undefined;
  const isFree = isTemplateFreeAtSource({
    templateType: scheduledWapiTemplate?.template_type ?? '',
    isChatActive: selectedChat?.state === ChatState.ACTIVE,
    hasFreeEntryPoint: !!selectedChat?.freeEntryPointData?.hasFreeEntryPoint,
    at: triggerAt,
  });

  function handleClose() {
    setScheduledWapiTemplate(undefined);
    setTriggerDate(undefined);
    setTriggerTime(undefined);
    setSelectedAction(ChatEnum.WABTEMPLATES);
  }

  // INT-1500 U13: the lead opted out of marketing (131050), so the warning
  // offers the picker on Seguimiento, still in scheduling mode.
  function handleUseUtilityTemplate() {
    setScheduledWapiTemplate(undefined);
    setTriggerDate(undefined);
    setTriggerTime(undefined);
    setIsSchedulePickerMode(true);
    setTemplatePickerRequest({ templateType: 'UTILITY' });
  }

  return (
    <div className="WabTemplateSchedule">
      {/* Header row */}
      <div className="WabTemplateSchedule__Header">
        <div className="WabTemplateSchedule__Left">
          <strong className="text-sm text-primary whitespace-nowrap">
            {t('wabTemplateScheduleAction.title')}
          </strong>
          <span
            className={`text-xs rounded-full px-2 py-1 text-white whitespace-nowrap ${
              isUtility ? 'bg-[#111827]' : 'bg-primary'
            }`}
          >
            {isUtility
              ? t('wabTemplateScheduleAction.badge.utility')
              : t('wabTemplateScheduleAction.badge.marketing')}
          </span>
          <span
            className={`text-xs rounded-full px-2 py-1 text-white whitespace-nowrap ${
              isFree ? 'bg-[#059669]' : 'bg-[#3B82F6]'
            }`}
          >
            {isFree
              ? t('wabTemplateScheduleAction.badge.free')
              : t('wabTemplateScheduleAction.badge.cost')}
          </span>
          <span className="text-sm font-semibold text-base-content truncate">
            {scheduledWapiTemplate?.name}
          </span>
        </div>
      </div>

      <hr className="border-base-content/20" />

      {/* Date + time row */}
      <div className="WabTemplateSchedule__DateRow">
        <strong className="text-sm whitespace-nowrap">
          {t('wabTemplateScheduleAction.scheduleOn')}
        </strong>
        <div className="WabTemplateSchedule__InputContainer">
          <input
            value={triggerDate ?? ''}
            min={localDate}
            onChange={(e) => setTriggerDate(e.target.value)}
            className="WabTemplateSchedule__InputContainer__Input"
            type="date"
          />
        </div>
        <strong className="text-sm whitespace-nowrap">
          {t('wabTemplateScheduleAction.scheduleAt')}
        </strong>
        <div className="WabTemplateSchedule__InputContainer">
          <input
            value={triggerTime ?? ''}
            onChange={(e) => setTriggerTime(e.target.value)}
            className="WabTemplateSchedule__InputContainer__Input"
            type="time"
          />
        </div>
      </div>

      {/* Variables + preview row */}
      <div className="WabTemplateSchedule__Body">
        {hasVariables && (
          <div className="WabTemplateSchedule__Variables">
            {parameters.map((p) => {
              const isEmpty = !paramValues[p.name]?.trim();
              const showError = touchedParams[p.name] && isEmpty;
              return (
                <div key={p.name} className="WabTemplateSchedule__Variables__Field">
                  <label
                    className={`text-xs whitespace-nowrap ${
                      showError ? 'text-error' : 'text-base-content/60'
                    }`}
                  >{`{{${p.name}}}`}</label>
                  <input
                    placeholder={p.name}
                    value={paramValues[p.name] ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleParamChange(p.name, e.target.value)
                    }
                    onBlur={() => handleParamBlur(p.name)}
                    className={`WabTemplateSchedule__Variables__Input ${
                      showError ? 'border-error focus:border-error' : ''
                    }`}
                  />
                  {showError && (
                    <span className="text-xs text-error">{t('wabTemplateScheduleAction.requiredField')}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Preview */}
        <div className="WabTemplateSchedule__Preview">
          {previewHeader && !previewHeader.startsWith('https://') && (
            <p className="WabTemplateSchedule__Preview__Header">{previewHeader}</p>
          )}
          {previewHeader?.startsWith('https://') && (
            <img
              src={previewHeader}
              alt="header"
              className="WabTemplateSchedule__Preview__Image"
            />
          )}
          <p className="WabTemplateSchedule__Preview__Body">{previewBody}</p>
        </div>
      </div>

      {/* Past date warning */}
      {isDateInPast && (
        <p className="text-xs text-error px-4 py-1">
          {t('wabTemplateScheduleAction.pastDateError')}
        </p>
      )}

      {/* BUG-818: the template needs a header image we do not host, and this
          modal has no uploader — say so and point at the send that does,
          rather than leaving a silently disabled button. */}
      {headerMediaMissing && (
        <p className="text-xs text-error px-4 py-1">
          {t('wabTemplateScheduleAction.headerMediaMissing')}
        </p>
      )}

      <SchedulingRiskWarning
        triggerDate={triggerDate}
        triggerTime={triggerTime}
        content={{ kind: 'template', category: scheduledWapiTemplate?.template_type }}
        onScheduleAnyway={handleScheduleTemplate}
        isScheduleDisabled={isScheduleDisabled}
        onChangeTrigger={({ date, time }) => {
          setTriggerDate(date);
          setTriggerTime(time);
        }}
        onUseUtilityTemplate={handleUseUtilityTemplate}
      />

      {/* Amber note — always visible */}
      <div className="WabTemplateSchedule__CostWarning">
        <ExclamationTriangleIcon className="WabTemplateSchedule__CostWarning__Icon" />
        <span>{t('wabTemplateScheduleAction.noDeleteNote')}</span>
      </div>

      {/* Footer */}
      <div className="WabTemplateSchedule__Footer">
        <button onClick={handleClose} className="btn btn-ghost btn-sm">
          {t('wabTemplateScheduleAction.cancel')}
        </button>
        <button
          onClick={handleScheduleTemplate}
          disabled={isScheduleDisabled}
          className="btn btn-primary btn-sm"
        >
          {loading ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            t('wabTemplateScheduleAction.saveButton')
          )}
        </button>
      </div>
    </div>
  );
}
